import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import AuthComponent from './components/AuthComponent';
import AppShell from './components/AppShell';
import AddReceiptPage from './pages/AddReceiptPage';
import BlockyPage from './pages/BlockyPage';
import ProfilPage from './pages/ProfilPage';
import MarketingEmailsPage from './pages/MarketingEmailsPage';
import VaseDataPage from './pages/VaseDataPage';
import MojeSkupinyPage from './pages/MojeSkupinyPage';
import MojiLudiaPage from './pages/MojiLudiaPage';
import ReceiptDetailPage from './pages/ReceiptDetailPage';
import LegalDocumentPage from './pages/LegalDocumentPage';
import LegalDocumentsPage from './pages/LegalDocumentsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import CookieConsent from 'react-cookie-consent';
import { initPostHog, hasAnalyticsConsent, hasDeclinedAnalytics } from './utils/posthog';

function App() {
  // Check if user already gave consent on previous visit
  useEffect(() => {
    if (hasAnalyticsConsent()) {
      initPostHog(true); // Identified tracking
    } else if (hasDeclinedAnalytics()) {
      initPostHog(false); // Anonymous tracking
    }
    // If neither, wait for cookie banner interaction
  }, []);

  return (
    <>
      <BrowserRouter>
        <Routes>
          {/* Public legal document viewer - accessible without authentication */}
          <Route path="legal/:type" element={<LegalDocumentPage />} />

          {/* Custom native auth screens - public, no MSAL redirect auth needed */}
          <Route path="auth/login" element={<LoginPage />} />
          <Route path="auth/register" element={<RegisterPage />} />
          <Route path="auth/reset-password" element={<ResetPasswordPage />} />

          {/* Authenticated routes */}
          <Route path="*" element={
            <AuthComponent>
              <Routes>
                <Route path="/" element={<AppShell />}>
                  {/* Default redirect to /add */}
                  <Route index element={<Navigate to="/add" replace />} />

                  {/* Main navigation pages */}
                  <Route path="add" element={<AddReceiptPage />} />
                  <Route path="receipts" element={<BlockyPage />} />
                  <Route path="analytics" element={<AnalyticsPage />} />
                  <Route path="profile" element={<ProfilPage />} />

                  {/* Profile sub-pages */}
                  <Route path="profile/marketing-emails" element={<MarketingEmailsPage />} />
                  <Route path="profile/vase-data" element={<VaseDataPage />} />
                  <Route path="profile/legal-documents" element={<LegalDocumentsPage />} />
                  <Route path="profile/moje-skupiny" element={<MojeSkupinyPage />} />
                  <Route path="profile/moje-skupiny/moji-ludia" element={<MojiLudiaPage />} />

                  {/* Receipt detail page */}
                  <Route path="receipts/:receiptId" element={<ReceiptDetailPage />} />

                  {/* Catch all - redirect to add */}
                  <Route path="*" element={<Navigate to="/add" replace />} />
                </Route>
              </Routes>
            </AuthComponent>
          } />
        </Routes>
      </BrowserRouter>

      {/* GDPR Cookie Consent Banner - shown everywhere (login + authenticated pages) */}
      <CookieBanner />
    </>
  );
}

function CookieBanner() {
  return (
    <CookieConsent
      location="bottom"
      buttonText="Prijať všetky"
      declineButtonText="Odmietnuť"
      enableDeclineButton
      cookieName="smartblocek_cookie_consent"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '15px 20px',
        alignItems: 'center',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
        zIndex: 9999
      }}
      buttonStyle={{
        background: '#fff',
        color: '#667eea',
        fontSize: '14px',
        fontWeight: '600',
        borderRadius: '8px',
        padding: '10px 24px',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
      declineButtonStyle={{
        background: 'rgba(255,255,255,0.2)',
        color: '#fff',
        fontSize: '14px',
        fontWeight: '500',
        borderRadius: '8px',
        padding: '10px 24px',
        border: '1px solid rgba(255,255,255,0.3)',
        cursor: 'pointer',
        marginRight: '10px'
      }}
      expires={365}
      onAccept={() => {
        console.log('User accepted analytics consent');
        initPostHog(true);
      }}
      onDecline={() => {
        console.log('User declined analytics consent - using anonymous tracking');
        initPostHog(false);
      }}
    >
      <span style={{ fontSize: '14px', lineHeight: '1.6' }}>
        Táto webová stránka používa nevyhnutné súbory cookies na zabezpečenie autentifikácie a funkčnosti aplikácie.
        Na zlepšenie aplikácie zbierame anonymizované analytické údaje. Ak súhlasíte, môžeme tieto údaje prepojiť s vaším profilom pre lepšie personalizované zážitky.
        {' '}
        <a
          href="/legal/cookies"
          style={{
            color: '#fff',
            textDecoration: 'underline',
            fontWeight: '500'
          }}
        >
          Viac info
        </a>
      </span>
    </CookieConsent>
  );
}

export default App;
